import { Component, Input, signal, computed, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';

export type LoadingState = 'loading' | 'error' | 'not-found' | 'empty';

export interface LoadingConfig {
  state?: LoadingState;
  title?: string;
  message?: string;
  icon?: string;
  showRetry?: boolean;
  customContent?: boolean;
}

@Component({
    selector: 'app-page-not-found',
    templateUrl: './page-not-found.component.html',
    styleUrls: ['./page-not-found.component.css'],
    standalone: true,
    imports: [
      CommonModule,
      MatProgressSpinnerModule,
      MatIconModule,
      MatButtonModule
    ],
})
export class PageNotFoundComponent implements OnInit {
  @Input() config: LoadingConfig = {};
  
  // Signals for reactive state management
  readonly currentState = signal<LoadingState>('loading');
  readonly isLoading = computed(() => this.currentState() === 'loading');
  readonly showSpinner = computed(() => this.isLoading());
  
  // Computed configuration based on state
  readonly displayConfig = computed(() => {
    const state = this.currentState();
    const defaultConfigs: Record<LoadingState, LoadingConfig> = {
      loading: {
        title: 'Loading Application',
        message: 'Loading, please wait...',
        icon: 'hourglass_empty',
        showRetry: false
      },
      error: {
        title: 'Something went wrong',
        message: 'We encountered an unexpected error. Please try again.',
        icon: 'error_outline',
        showRetry: true
      },
      'not-found': {
        title: 'Page Not Found',
        message: 'The page you are looking for does not exist or has been moved.',
        icon: 'search_off',
        showRetry: false
      },
      empty: {
        title: 'No Data Available',
        message: 'There is no data to display at the moment.',
        icon: 'inbox',
        showRetry: false
      }
    };

    return {
      ...defaultConfigs[state],
      ...this.config,
      state
    };
  });

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    // Set initial state based on input or route
    if (this.config.state) {
      this.currentState.set(this.config.state);
    }
    
    // Log route information for debugging
    console.log('Route children:', this.route.children);
    console.log('Current state:', this.currentState());
  }

  // Method to change state programmatically
  setState(state: LoadingState): void {
    this.currentState.set(state);
  }

  // Retry functionality
  onRetry(): void {
    this.currentState.set('loading');
    // Emit retry event or trigger parent component action
    // This can be enhanced with an EventEmitter if needed
    window.location.reload();
  }

  // Go back functionality
  onGoBack(): void {
    window.history.back();
  }

  // Go home functionality
  onGoHome(): void {
    window.location.href = '/';
  }
}
